import * as React from 'react';
import type { AgentContinuation } from '@cdf/api-client-react';
import { useAuth } from '@/components/auth/auth-provider';
import { useTenantEntity } from '@/components/access/tenant-entity-provider';

export type NativePrefill = NonNullable<AgentContinuation['prefill']>;
export type PrefillIdentity = { actorId: string; tenantId: string; entityId: string };
type Transfer = PrefillIdentity & { actionKey: string; destination: string; values: NativePrefill; expiresAt: number };
let transfer: Transfer | null = null;
let version = 0;
const listeners = new Set<() => void>();
const notify = () => { version++; listeners.forEach(listener => listener()); };
const same = (left: PrefillIdentity, right: PrefillIdentity) =>
  left.actorId === right.actorId && left.tenantId === right.tenantId && left.entityId === right.entityId;
const destinations: Record<string, readonly string[]> = {
  'entity.crm.create_account': ['/partner-engagement/accounts'],
  'entity.campaign.save_draft': ['/content-hub/mailing-list'],
  'entity.content.save_asset_draft': ['/content-hub/authority', '/content-hub/authority?kind=faq', '/content-hub/authority?kind=case_study', '/content-hub/lead-magnet', '/content-hub/operations'],
  'entity.research.create_thread': ['/research'],
  'entity.research.save_document': ['/research?view=documents']
};

/** One short-lived, data-only, in-memory transfer. No storage, URL text or save authority. */
export function stageAgentNativePrefill(identity: PrefillIdentity, choice: AgentContinuation): boolean {
  if (!identity.actorId || !choice.prefill || choice.scope.kind !== 'entity' ||
    identity.tenantId !== choice.scope.tenantId || identity.entityId !== choice.scope.entityId ||
    !destinations[choice.actionKey]?.includes(choice.destination) || JSON.stringify(choice.prefill).length > 64000) return false;
  if (transfer && transfer.expiresAt > Date.now()) return false;
  transfer = { ...identity, actionKey: choice.actionKey, destination: choice.destination,
    values: structuredClone(choice.prefill), expiresAt: Date.now() + 10 * 60_000 };
  notify();
  return true;
}
export function clearAgentNativePrefill() { transfer = null; notify(); }
export function takeAgentNativePrefill(identity: PrefillIdentity, actionKey: string, destination: string): NativePrefill | null {
  if (!transfer) return null;
  if (transfer.expiresAt <= Date.now() || !same(transfer, identity)) { clearAgentNativePrefill(); return null; }
  if (transfer.actionKey !== actionKey || transfer.destination !== destination) return null;
  const values = transfer.values;
  clearAgentNativePrefill();
  return values;
}
/** Mounted above native routes so sign-out/scope switches also discard unused transfers. */
export function useAgentNativePrefillBoundary() {
  const auth = useAuth();
  const access = useTenantEntity();
  const actor = auth.account?.stableId ?? '';
  const tenant = access.selected?.tenantId ?? '';
  const entity = access.selected?.entityId ?? '';
  React.useEffect(() => () => clearAgentNativePrefill(), [actor, tenant, entity]);
}
export function useAgentNativePrefill(actionKey: string, canApply: boolean, apply: (values: NativePrefill) => void, kind?: NativePrefill['kind']) {
  const auth = useAuth();
  const access = useTenantEntity();
  const currentVersion = React.useSyncExternalStore(
    React.useCallback(listener => { listeners.add(listener); return () => { listeners.delete(listener); }; }, []),
    () => version, () => 0
  );
  const callback = React.useRef(apply);
  React.useEffect(() => { callback.current = apply; });
  const actorId = auth.account?.stableId ?? '';
  const tenantId = access.selected?.tenantId ?? '';
  const entityId = access.selected?.entityId ?? '';
  React.useEffect(() => {
    if (transfer && (!actorId || !same(transfer, { actorId, tenantId, entityId }))) clearAgentNativePrefill();
    const permission = actionKey.includes('.campaign.') || actionKey.includes('.content.') ? 'entity.content.manage' : 'entity.records.write';
    if (!canApply || !actorId || access.status !== 'ready' || !access.hasPermission(permission) || (kind && transfer?.values.kind !== kind)) return;
    const values = takeAgentNativePrefill({ actorId, tenantId, entityId }, actionKey, transfer?.destination.includes('?') ? window.location.pathname + window.location.search : window.location.pathname);
    if (values) callback.current(values);
  }, [currentVersion, actorId, tenantId, entityId, actionKey, canApply, access.status, access, kind]);
}
